import { TTypeOfCodec, TTypeOfNewDefault } from "../../shared/src/codecs/codec";
import { array } from "../../shared/src/codecs/types/array";
import { boolean } from "../../shared/src/codecs/types/boolean";
import { intersection } from "../../shared/src/codecs/types/intersection";
import { dateTime } from "../../shared/src/codecs/types/dateTime";
import { literal } from "../../shared/src/codecs/types/literal";
import { nullCodec } from "../../shared/src/codecs/types/nullCodec";
import { partial } from "../../shared/src/codecs/types/partial";
import { required } from "../../shared/src/codecs/types/required";
import { string } from "../../shared/src/codecs/types/string";
import { union } from "../../shared/src/codecs/types/union";

export const CaseQuickSummaryConfidence = union([
    literal("low"),
    literal("medium"),
    literal("high"),
]);
export type TCaseQuickSummaryConfidence = TTypeOfCodec<typeof CaseQuickSummaryConfidence>;

export const CaseQuickSummaryWaitingOnOwner = union([
    literal("client"),
    literal("other_side"),
    literal("third_party"),
    literal("us"),
]);
export type TCaseQuickSummaryWaitingOnOwner = TTypeOfCodec<typeof CaseQuickSummaryWaitingOnOwner>;

export const CaseQuickSummaryWaitingOnSubject = union([
    required({
        kind: literal("roadblock"),
        roadblock_id: string(),
    }),
    required({ kind: literal("enquiries") }),
    required({ kind: literal("documents") }),
    required({ kind: literal("ad_hoc_jobs") }),
    required({ kind: literal("inbox_emails") }),
    required({ kind: literal("complaint") }),
]);
export type TCaseQuickSummaryWaitingOnSubject = TTypeOfCodec<typeof CaseQuickSummaryWaitingOnSubject>;

// `subject` is partial, not required: waiting_on is stored jsonb and every row
// written before subjects existed would otherwise fail to decode — which would
// break the introducer read path, not just this card.
export const CaseQuickSummaryWaitingOnItem = intersection([
    required({
        owner: CaseQuickSummaryWaitingOnOwner,
        label: string(),
        detail: union([nullCodec(), string()]),
        due_date: union([nullCodec(), string()]),
        assignee_name: union([nullCodec(), string()]),
    }),
    partial({
        subject: CaseQuickSummaryWaitingOnSubject,
    }),
]);
export type TCaseQuickSummaryWaitingOnItem = TTypeOfCodec<typeof CaseQuickSummaryWaitingOnItem>;

export const CaseQuickSummaryActivityItem = required({
    label: string(),
    occurred_at: string(),
    actor: union([nullCodec(), string()]),
});
export type TCaseQuickSummaryActivityItem = TTypeOfCodec<typeof CaseQuickSummaryActivityItem>;

export const CaseQuickSummaryCommsContact = required({
    role: string(),
    sent_at: string(),
});
export type TCaseQuickSummaryCommsContact = TTypeOfCodec<typeof CaseQuickSummaryCommsContact>;

export const CaseQuickSummaryComms = required({
    digest: string(),
    last_inbound: union([nullCodec(), CaseQuickSummaryCommsContact]),
    last_outbound: union([nullCodec(), CaseQuickSummaryCommsContact]),
});
export type TCaseQuickSummaryComms = TTypeOfCodec<typeof CaseQuickSummaryComms>;

export const CaseQuickSummaryWhatsNextItem = required({
    owner: union([nullCodec(), CaseQuickSummaryWaitingOnOwner]),
    action: string(),
});
export type TCaseQuickSummaryWhatsNextItem = TTypeOfCodec<typeof CaseQuickSummaryWhatsNextItem>;

export const CaseCompletedEvent = required({
    source_type: union([
        literal("roadblock"),
        literal("document"),
        literal("job"),
        literal("enquiry"),
        literal("milestone")
    ]),
    description: string(),
    completed_at: string(),
    completed_by_name: union([nullCodec(), string()]),
});
export type TCaseCompletedEvent = TTypeOfCodec<typeof CaseCompletedEvent>;

export const CaseQuickSummary = required({
    id: string(),
    case_id: string(),
    headline: string(),
    predicted_completion_month: union([nullCodec(), string()]),
    predicted_completion_confidence: union([nullCodec(), CaseQuickSummaryConfidence]),
    blocking: array(string()),
    whats_next: array(string()),
    waiting_on: array(CaseQuickSummaryWaitingOnItem),
    recent_activity: array(CaseQuickSummaryActivityItem),
    comms_summary: union([nullCodec(), CaseQuickSummaryComms]),
    whats_next_v2: union([nullCodec(), array(CaseQuickSummaryWhatsNextItem)]),
    model_name: string(),
    generated_at: dateTime(),
    is_stale: boolean(),
});
export type TCaseQuickSummary = TTypeOfCodec<typeof CaseQuickSummary>;
export type TCaseQuickSummaryNewDefault = TTypeOfNewDefault<typeof CaseQuickSummary>;

export const CaseQuickSummaryClaudeOutput = required({
    headline: string(),
    predicted_completion_month: union([nullCodec(), string()]),
    predicted_completion_confidence: union([nullCodec(), CaseQuickSummaryConfidence]),
    whats_next: array(CaseQuickSummaryWhatsNextItem),
    comms_digest: union([nullCodec(), string()]),
});
export type TCaseQuickSummaryClaudeOutput = TTypeOfCodec<typeof CaseQuickSummaryClaudeOutput>;
